import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Snackbar,
  Modal,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Google,
  Facebook,
  LinkedIn,
} from "@mui/icons-material";
import { loginUser, clearError } from "../redux/slices/authSlice";
import "./Login.css";

import authService from "../utils/authService";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated, userType } = useSelector(
    (state) => state.auth,
  );

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && userType) {
      // Redirect to the page user was trying to access, or default to home page
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, userType, navigate, location.state]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "rememberMe" ? checked : value,
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Format d'email invalide";
    }

    if (!formData.password.trim()) {
      errors.password = "Le mot de passe est requis";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    dispatch(
      loginUser({
        email: formData.email,
        password: formData.password,
      }),
    );
  };

  // Fonction pour personnaliser le message d'erreur
  const getCustomErrorMessage = (error) => {
    if (error === "Identifiants invalides") {
      return "Votre email ou mot de passe est incorrect";
    }
    return error;
  };

  const handleSocialLogin = (provider) => {
    // Mock social login
    console.log(`Connexion via ${provider} (mock)`);
    // In real implementation, this would redirect to OAuth provider
  };

  const handlePasswordReset = async () => {
    if (!resetEmail.trim() || !/\S+@\S+\.\S+/.test(resetEmail)) {
      return;
    }

    setResetLoading(true);
    setResetError("");

    try {
      await authService.forgotPassword(resetEmail);
      setResetSuccess(true);
      setResetModalOpen(false);
      setResetEmail("");
    } catch (err) {
      setResetError(
        err.response?.data?.message ||
          "Une erreur est survenue lors de la demande de réinitialisation.",
      );
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseError = () => {
    dispatch(clearError());
    setResetError("");
  };

  const handleCloseResetSuccess = () => {
    setResetSuccess(false);
  };

  return (
    <>
      <Helmet>
        <title>Connexion - Mission Sport</title>
        <meta
          name="description"
          content="Connectez-vous à votre compte Mission Sport pour accéder à votre tableau de bord et gérer vos missions sportives."
        />
      </Helmet>

      <Box className="login-page">
        <Container maxWidth="md" className="login-container">
          <Paper className="login-paper" elevation={3}>
            <Box className="login-header">
              <Typography variant="h4" component="h1" className="login-title">
                Connexion
              </Typography>
              <Typography variant="body1" className="login-subtitle">
                Accédez à votre espace Mission Sport
              </Typography>
            </Box>

            <form onSubmit={handleSubmit} className="login-form">
              <TextField
                fullWidth
                name="email"
                type="email"
                label="Email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                className="login-field"
                aria-label="Email"
              />

              <TextField
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                label="Mot de passe"
                value={formData.password}
                onChange={handleInputChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                className="login-field"
                aria-label="Mot de passe"
              />

              <Box className="login-options">
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label="Se souvenir de moi"
                  className="remember-me"
                />

                <Button
                  variant="text"
                  onClick={() => setResetModalOpen(true)}
                  className="forgot-password"
                >
                  Mot de passe oublié ?
                </Button>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                className="login-button"
                aria-label="Se connecter"
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={20}
                      color="inherit"
                      style={{ marginRight: 8 }}
                    />
                    Connexion...
                  </>
                ) : (
                  "Se connecter"
                )}
              </Button>
            </form>

            <Box className="login-divider">
              <Typography variant="body2" className="divider-text">
                ou
              </Typography>
            </Box>

            <Box className="social-buttons-container">
              <Button
                variant="outlined"
                startIcon={<Google />}
                onClick={() => handleSocialLogin("Google")}
                className="social-button social-button-google"
                aria-label="Se connecter avec Google"
              >
                Google
              </Button>

              <Button
                variant="outlined"
                startIcon={<Facebook />}
                onClick={() => handleSocialLogin("Facebook")}
                className="social-button social-button-facebook"
                aria-label="Se connecter avec Facebook"
              >
                Facebook
              </Button>

              <Button
                variant="outlined"
                startIcon={<LinkedIn />}
                onClick={() => handleSocialLogin("LinkedIn")}
                className="social-button social-button-linkedin"
                aria-label="Se connecter avec LinkedIn"
              >
                LinkedIn
              </Button>
            </Box>

            <Box className="login-footer">
              <Typography variant="body2" className="register-link">
                Pas encore inscrit ?{" "}
                <Link to="/register" className="link">
                  Créez un compte
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Container>

        {/* Password Reset Modal */}
        <Modal
          open={resetModalOpen}
          onClose={() => setResetModalOpen(false)}
          aria-labelledby="reset-modal-title"
        >
          <Box className="reset-modal">
            <Typography
              variant="h6"
              id="reset-modal-title"
              className="reset-modal-title"
            >
              Réinitialiser le mot de passe
            </Typography>

            <Typography variant="body2" className="reset-modal-description">
              Entrez votre adresse email pour recevoir un lien de
              réinitialisation.
            </Typography>

            <TextField
              fullWidth
              type="email"
              label="Adresse email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="reset-email-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Box className="reset-modal-actions">
              <Button
                variant="outlined"
                onClick={() => setResetModalOpen(false)}
                className="reset-cancel-button"
              >
                Annuler
              </Button>

              <Button
                variant="contained"
                onClick={handlePasswordReset}
                disabled={
                  !resetEmail.trim() ||
                  !/\S+@\S+\.\S+/.test(resetEmail) ||
                  resetLoading
                }
                className="reset-send-button"
              >
                {resetLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Envoyer le lien"
                )}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Error Snackbar */}
        <Snackbar
          open={!!error || !!resetError}
          autoHideDuration={6000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseError}
            severity="error"
            sx={{ width: "100%" }}
          >
            {getCustomErrorMessage(error) || resetError}
          </Alert>
        </Snackbar>

        {/* Reset Success Snackbar */}
        <Snackbar
          open={resetSuccess}
          autoHideDuration={4000}
          onClose={handleCloseResetSuccess}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseResetSuccess}
            severity="success"
            sx={{ width: "100%" }}
          >
            Lien de réinitialisation envoyé ! Vérifiez votre boîte email.
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default Login;
